import { ConfigurationSourceDataMap, ConfigurationStatus, CoordinatingGroup, Institution, MasterGroup, Metadata, SourceDataFinding, SourceDataFindingStatus, Participant, SourceData, Trial } from "@/api/dtos";
import sourceDataFindingStatusToText from "@/utilities/configuration/sourceDataFindingStatusToText";
import { Box, Button, Chip, Divider, ListItem, ListItemIcon, ListItemText, Paper, Theme, alpha, styled } from "@mui/material";
import { DateTime } from "luxon";
import { FC, useCallback, useMemo, useState } from "react";
import SourceDataFindingDetails from "./SourceDataFindingDetails";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import sourceDataFindingTypeToText from "@/utilities/configuration/sourceDataFindingTypeToText";
import { StatusListItemTooltip } from "../status/StatusListStats";
import { AppSettings } from "@/AppSettings";
import sourceDataFindingStatusToColor from "@/utilities/configuration/sourceDataFindingStatusToColor";
import { Link } from "react-router-dom";


const SourceDataFindingContainer = styled(Paper)(({ theme }) => ({
    marginBottom: theme.spacing(2)
}));

const ChipBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    gap: theme.spacing(2),
    marginLeft: 'auto'
}));

const StatusChip = styled(Chip)(({ theme }) => ({
    height: theme.spacing(3),
    minWidth: theme.spacing(18.75)
}));

const DateChip = styled(Chip)(({ theme }) => ({
    height: theme.spacing(3),
    minWidth: theme.spacing(15)
}));

const UserChip = styled(Chip)(({ theme }) => ({
    height: theme.spacing(3),
    minWidth: theme.spacing(15)
}));

const IndentBox = styled(Box)(({ theme }) => ({
    marginLeft: theme.spacing(8)
}));

const IndentBorderBox = styled(Box)(({ theme }) => ({
    borderLeft: `1px solid ${alpha(theme.palette.mode === 'dark' ? theme.palette.common.white : theme.palette.common.black, 0.12)}`
}));

const IndentDivider = styled(Divider)(({ theme }) => ({
    borderColor: alpha(theme.palette.mode === 'dark' ? theme.palette.common.white : theme.palette.common.black, 0.12)
}));

const StyledListItem = styled(ListItem)(({ theme }) => ({
    padding: theme.spacing(0, 2),
    minHeight: theme.spacing(6),
    whiteSpace: 'pre-wrap'
}));

const StyledListItemText = styled(ListItemText)(({ theme }) => ({
    margin: theme.spacing(0.5, 0)
}));

const StyledListItemIcon = styled(ListItemIcon)(({ theme }) => ({
    minWidth: theme.spacing(6)
}));

const Buttons = styled(Box)(({ theme }) => ({
    marginBottom: theme.spacing(2),
    display: 'flex',
    gap: theme.spacing(2),
    justifyContent: 'flex-end',
    padding: theme.spacing(2)

}));

interface ISourceDataFindingViewProps {
    trialId?: number;
    masterGroupId?: number;
    coordinatingGroupId?: number;
    institutionId?: number;
    participantId?: number;
    studyEventOid?: string;
    studyEventRepeat?: number;
    caseReportFormOid?: string;
    caseReportFormVersionOid?: string;
    itemGroupName?: string;
    itemGroupRepeat?: number;
    itemName?: string;

    theme: Theme;
    trials?: Record<number, Trial>;
    masterGroups?: Record<number, MasterGroup>;
    coordinatingGroups?: Record<number, CoordinatingGroup>;
    institutions?: Record<number, Institution>;
    participants?: Record<number, Participant>;
    metadata?: Metadata;
    trialConfiguration?: ConfigurationSourceDataMap;
    configuration?: ConfigurationSourceDataMap;
    sourceData?: SourceData;

    data: SourceDataFinding;
}

const SourceDataFindingView: FC<ISourceDataFindingViewProps> = ({
    data,
    ...context
}) => {
    const initialStatus = data.status === SourceDataFindingStatus.Resolved ? SourceDataFindingStatus.Pending : data.status;

    const initialStatusText = sourceDataFindingStatusToText(initialStatus)
    const initialDate = data.createdDate ? DateTime.fromISO(data.createdDate).toFormat('dd/MM/yyyy') : undefined;

    const resolvedStatus = data.status;
    const resolvedStatusText = sourceDataFindingStatusToText(resolvedStatus);
    const resolvedDate = data.resolvedDate ? DateTime.fromISO(data.resolvedDate).toFormat('dd/MM/yyyy') : undefined;

    const [ editStatus, setEditStatus ] = useState<SourceDataFindingStatus>();
    const onEditCancel = useCallback(() => {
        setEditStatus(undefined);
    }, [setEditStatus]);

    const {
        theme,

        trials,
        masterGroups,
        coordinatingGroups,
        institutions,
        participants,
        metadata,
        trialConfiguration,
        configuration,
        sourceData
    } = context;

    const contextStatus = useMemo(() => {
        if (data.participantId) {
            return participants?.[data.participantId]?.status ?? ConfigurationStatus.Disabled;
        }

        if (data.institutionId) {
            return institutions?.[data.institutionId]?.status ?? ConfigurationStatus.Disabled;
        }

        if (data.coordinatingGroupId) {
            return coordinatingGroups?.[data.coordinatingGroupId]?.status ?? ConfigurationStatus.Disabled;
        }

        if (data.masterGroupId) {
            return masterGroups?.[data.masterGroupId]?.status ?? ConfigurationStatus.Disabled;
        }

        if (data.trialId) {
            return trials?.[data.trialId]?.status ?? ConfigurationStatus.Disabled;
        }
    }, [
        trials,
        masterGroups,
        coordinatingGroups,
        institutions,
        participants,

        data
    ])


    return (
        <SourceDataFindingContainer>
            <SourceDataFindingDetails 
                trialId={data.trialId}
                masterGroupId={data.masterGroupId}
                coordinatingGroupId={data.coordinatingGroupId}
                institutionId={data.institutionId}
                participantId={data.participantId}
                studyEventOid={data.studyEventOid}
                studyEventRepeat={data.studyEventRepeat}
                caseReportFormOid={data.caseReportFormOid}
                caseReportFormVersionOid={data.caseReportFormVersionOid}
                itemGroupName={data.itemGroupName}
                itemGroupRepeat={data.itemGroupRepeat}
                itemName={data.itemName}

                trials={trials}
                masterGroups={masterGroups}
                coordinatingGroups={coordinatingGroups}
                institutions={institutions}
                participants={participants}
                metadata={metadata}
                trialConfiguration={trialConfiguration}
                configuration={configuration}
                sourceData={sourceData}

                hideTrial={!!context.trialId}
                hideMasterGroup={!!context.masterGroupId}
                hideCoordinatingGroup={!!context.coordinatingGroupId}
                hideInstitution={!!context.institutionId}
                hideParticipant={!!context.participantId}
                hideStudyEvent={!!context.studyEventOid}
                hideCaseReportForm={!!context.caseReportFormOid}
                hideItemGroup={!!context.itemGroupName}
                hideItem={!!context.itemName}
            />
            <StyledListItem>
                <StyledListItemIcon>
                    <FontAwesomeIcon size="lg" icon={AppSettings.icons.finding} />
                </StyledListItemIcon>
                <StyledListItemText 
                    primary={sourceDataFindingTypeToText(data.type)}
                />
                <ChipBox>
                    <StatusListItemTooltip
                        title={
                            <>
                                <strong>Status</strong><br />
                                {resolvedStatusText}
                            </>
                        }
                    >
                        <StatusChip label={resolvedStatusText} color={sourceDataFindingStatusToColor(resolvedStatus)} />
                    </StatusListItemTooltip>
                    {
                        initialDate && (
                            <StatusListItemTooltip
                                title={
                                    <>
                                        <strong>Date Created</strong><br />
                                        {initialDate}
                                    </>
                                }
                            >
                                <DateChip icon={<FontAwesomeIcon icon={AppSettings.icons.itemValueDate} />} label={initialDate} />
                            </StatusListItemTooltip>
                        )
                    }
                    <StatusListItemTooltip
                        title={
                            <>
                                <strong>Created By</strong><br />
                                {data.creator}
                            </>
                        }
                    >
                        <UserChip icon={<FontAwesomeIcon icon={AppSettings.icons.user} />}  label={data.creatorUsername} />
                    </StatusListItemTooltip>
                </ChipBox>
            </StyledListItem>
            <Divider />
            <IndentBox> 
                <IndentBorderBox>
                    <StyledListItem>
                        <StyledListItemText 
                            primary={<><strong>Reference:</strong> {data.reference}</>}
                        />
                        <ChipBox>
                            <StatusListItemTooltip
                                title={
                                    <>
                                        <strong>Status</strong><br />
                                        {initialStatusText}
                                    </>
                                }
                            >
                                <StatusChip label={initialStatusText} color={sourceDataFindingStatusToColor(initialStatus)} />
                            </StatusListItemTooltip>
                            {
                                initialDate && (
                                    <StatusListItemTooltip
                                        title={
                                            <>
                                                <strong>Date Created</strong><br />
                                                {initialDate}
                                            </>
                                        }
                                    >
                                        <DateChip icon={<FontAwesomeIcon icon={AppSettings.icons.itemValueDate} />} label={initialDate} />
                                    </StatusListItemTooltip>
                                )
                            }
                            <StatusListItemTooltip
                                title={
                                    <>
                                        <strong>Created By</strong><br />
                                        {data.creator}
                                    </>
                                }
                            >
                                <UserChip icon={<FontAwesomeIcon icon={AppSettings.icons.user} />}  label={data.creatorUsername} />
                            </StatusListItemTooltip>
                        </ChipBox>
                    </StyledListItem>
                    {
                        data.detail && (
                            <Divider />
                        )
                    }    
                </IndentBorderBox>
                {
                    data.detail && (
                        <IndentBox>
                            <IndentBorderBox>
                                <StyledListItem>
                                    {data.detail}
                                </StyledListItem>
                            </IndentBorderBox>
                        </IndentBox>
                    )
                }
            </IndentBox>
            {
                data.status === SourceDataFindingStatus.Resolved && (
                    <>
                        <IndentBox> 
                            <Divider />
                            <IndentBorderBox>
                                <StyledListItem>
                                    <StyledListItemText 
                                        primary={<><strong>Discussed With:</strong> {data.discussedWith}</>}
                                    />
                                    <ChipBox>
                                        <StatusListItemTooltip
                                            title={
                                                <>
                                                    <strong>Status</strong><br />
                                                    {resolvedStatusText}
                                                </>
                                            }
                                        >
                                            <StatusChip label={resolvedStatusText} color={sourceDataFindingStatusToColor(resolvedStatus)} />
                                        </StatusListItemTooltip>
                                        {
                                            resolvedDate && (
                                                <StatusListItemTooltip
                                                    title={
                                                        <>
                                                            <strong>Date Resolved</strong><br />
                                                            {resolvedDate}
                                                        </>
                                                    }
                                                >
                                                    <DateChip icon={<FontAwesomeIcon icon={AppSettings.icons.itemValueDate} />} label={resolvedDate} />
                                                </StatusListItemTooltip>
                                            )
                                        }
                                        <StatusListItemTooltip
                                            title={
                                                <>
                                                    <strong>Resolved By</strong><br />
                                                    {data.resolver}
                                                </>
                                            }
                                        >
                                            <UserChip icon={<FontAwesomeIcon icon={AppSettings.icons.user} />}  label={data.resolverUsername} />
                                        </StatusListItemTooltip>
                                    </ChipBox>
                                </StyledListItem>
                                {
                                    data.resolution && (
                                        <Divider />
                                    )
                                }    
                            </IndentBorderBox>
                            {
                                data.resolution && (
                                    <IndentBox>
                                        <IndentBorderBox>
                                            <StyledListItem>
                                                {data.resolution}
                                            </StyledListItem>
                                        </IndentBorderBox>
                                    </IndentBox>
                                )
                            }
                        </IndentBox>
                    </>
                )
            }

            {(contextStatus === ConfigurationStatus.Enabled && data.status === SourceDataFindingStatus.Pending) && <Divider />}
            {
                data.status === SourceDataFindingStatus.Pending && contextStatus === ConfigurationStatus.Enabled && (
                    <Buttons>
                        <Button
                            color="primary"
                            variant="contained"
                            component={Link}
                            to={`${location.pathname}/${data.id}/resolve`}
                        >
                            Resolve
                        </Button>
                        {/* <Button
                            color="error"
                            variant="contained"
                        >
                            Delete
                        </Button> */}
                    </Buttons>
                )
            }
        </SourceDataFindingContainer>
    )
}

export default SourceDataFindingView;